import { GridView } from '../grid/grid.view';
import { CSSProperties, useRef, useState } from 'react';
import { ColDef, GridApi } from 'ag-grid-community';
import { IHistory } from '@vivli/shared/infrastructure/interface';
import moment from 'moment';
import { GridCellRendererEnum } from '@vivli/shared/infrastructure/enum';
import { ButtonView } from '../buttons';
import { useDateComparatorHook } from '@vivli/shared/infrastructure/hook';
import { LoggerService } from '@vivli/shared/infrastructure/service';

const containerStyle: CSSProperties = {
    height: '100%',
    position: 'relative',
    backgroundColor: 'rgb(233, 234, 239)',
    display: 'grid',
    gridTemplateRows: 'auto 1fr',
    rowGap: '10px'
};

const exportBtnContainerStyle: CSSProperties = {
    width: '100%',
    display: 'flex',
    flexDirection: 'row-reverse',
    justifyContent: 'end'
};

const exportBtnStyle: CSSProperties = {
    padding: '8px',
    marginLeft: '10px',
    height: 'initial',
    width: '150px'
};

interface HistoryListViewProps {
    historyEntries: IHistory[];
    hideReviewers?: boolean;
    excludeComments?: boolean;
    includeOrgName?: boolean;
    showExportBtn?: boolean;
    hideFloatingFilter?: boolean;
    exportFileName?: string;
    maxClicked?: number;
    clickedColumnName?: string;
    displayOverTheLimitMessage?: () => void;
    downloadComparison?: (from: string, to: string) => void;
    isLoading?: boolean;
    autoHeight?: boolean;
}

export const HistoryListView = ({
    historyEntries,
    hideReviewers,
    excludeComments,
    includeOrgName,
    showExportBtn,
    hideFloatingFilter,
    exportFileName = 'history.csv',
    maxClicked,
    clickedColumnName = 'Compare',
    displayOverTheLimitMessage,
    downloadComparison,
    isLoading,
    autoHeight = true
}: HistoryListViewProps) => {
    const gridApiRef = useRef<GridApi>();
    const dateComparator = useDateComparatorHook();
    const hideCompare = !historyEntries?.some((entry) => entry.clicked != undefined);
    const [compareEnabled, setCompareEnabled] = useState<boolean>(false);

    const GetSelectedRows = (historyEntries: IHistory[]) => {
        return historyEntries.filter((entry) => entry.clicked == true);
    };
    const setEntriesClicked = (row: IHistory, checked: boolean): boolean => {
        const index = historyEntries.findIndex((entry) => entry.comment == row.comment);
        const clickedRows = GetSelectedRows(historyEntries);
        if (checked) {
            if (maxClicked && maxClicked > 0) {
                if (clickedRows.length >= maxClicked) {
                    displayOverTheLimitMessage && displayOverTheLimitMessage();
                    return false;
                }
            }
        }
        setCompareEnabled(
            (maxClicked && maxClicked > 0 && clickedRows.length >= maxClicked - 1 && checked) ||
                (clickedRows.length >= maxClicked + 1 && !checked)
        );
        return true;
    };
    const getFileName = (entry: IHistory) => {
        let fileName = entry.comment.split('DrSnapshot')[1].split('.pdf')[0];
        fileName = `DrSnapshot${fileName}.pdf`;
        return fileName;
    };

    const compareRows = (rows: IHistory[]) => {
        const clickedRows = GetSelectedRows(historyEntries);
        //Temp
        if (clickedRows.length == 2) {
            LoggerService.log(`Compare files: ${getFileName(clickedRows[0])} and  ${getFileName(clickedRows[1])} `);
            downloadComparison && downloadComparison(getFileName(clickedRows[0]), getFileName(clickedRows[1]));
        }
    };

    const gridCols: ColDef[] = [
        {
            headerName: 'Date and Time',
            field: 'dateTime',
            sort: 'asc',
            comparator: dateComparator,
            valueFormatter: ({ value }) => {
                const language = navigator.language;
                return language === 'en-US' ? moment(value).format('M/D/YY h:mm a') : moment(value).format('D/M/YY h:mm a');
            },
            maxWidth: 150
        },
        {
            headerName: 'Action',
            field: 'description',
            cellRenderer: GridCellRendererEnum.GridCellLongText
        },
        {
            headerName: 'Performed By',
            field: 'userName',
            cellRenderer: GridCellRendererEnum.GridCellLongText,
            valueFormatter: ({ value }) => {
                const noUserDisplay = 'N/A';
                return value?.includes(noUserDisplay) ? noUserDisplay : value;
            },
            maxWidth: 200
        },
        {
            headerName: 'Organization',
            field: 'orgName',
            cellRenderer: GridCellRendererEnum.GridCellLongText,
            maxWidth: 200,
            hide: !includeOrgName
        },
        {
            headerName: 'Comments',
            field: 'comment',
            cellRenderer: GridCellRendererEnum.GridCellLongText,
            cellRendererParams: ({ data }) => ({
                allowCopy: !data.onClick,
                copyText: 'Comments',
                onClick: data.onClick
            }),
            maxWidth: 300,
            hide: excludeComments
        },
        {
            headerName: clickedColumnName,
            field: 'clicked',
            cellRenderer: GridCellRendererEnum.GridCheckbox,
            cellRendererParams: ({ data }) => ({
                hide: data.clicked == undefined,
                readOnly: false,
                onChange: (checked: boolean, data: any) => {
                    const proceed = setEntriesClicked(data, checked);
                    if (proceed) {
                        data.clicked = checked;
                    }
                    return proceed;
                },
                style: { height: '20px', width: '20px' }
            }),

            maxWidth: 110,
            hide: hideCompare,
            filter: false
        }
    ];

    const grid = (
        <GridView
            rowData={historyEntries}
            columns={gridCols}
            fullWidthColumns={true}
            emptyMessage={'No history found.'}
            autoHeight={autoHeight}
            onGridReady={(api) => (gridApiRef.current = api)}
            hideFloatingFilter={hideFloatingFilter}
        />
    );

    return (
        <div style={containerStyle}>
            <div style={exportBtnContainerStyle}>
                <div></div>
                {showExportBtn && (
                    <ButtonView style={exportBtnStyle} onClick={() => gridApiRef.current?.exportDataAsCsv({ fileName: exportFileName })}>
                        Export History
                    </ButtonView>
                )}
                {!hideCompare && (
                    <ButtonView style={exportBtnStyle} disabled={!compareEnabled} onClick={compareRows} isLoading={isLoading}>
                        {clickedColumnName}
                    </ButtonView>
                )}
            </div>
            <div>{grid}</div>
        </div>
    );
};
